import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import PblCaseList from '@/components/Pbl/CaseList';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { useAppSelector } from '@/redux/store.hook';
import PATH from '@/router/path';
import { errorHandler } from '@/utils/toastHandler';

import { mapPblCaseToOcPendingReviewCase } from '../OcManageCases/caseReviewState';
import type { PblOcPendingReviewCase } from '../OcManageCases/types';
import { isLawyerCurrentCase, isPendingLawyerConfirmationCase } from './caseVisibility';
import { loadAllLawyerCases } from './loadAllLawyerCases';
import { useTranslation } from 'react-i18next';

const buildPendingDetailPath = (caseId: string) => PATH.pbl.lawyerPendingReviewDetail.replace(':caseId', caseId);

const PblLawyerPendingReviewList = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useAppSelector(getAuthSelector);
  const [rows, setRows] = useState<PblOcPendingReviewCase[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [currentCaseCount, setCurrentCaseCount] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const loadCases = async () => {
      try {
        const cases = await loadAllLawyerCases();

        if (!isMounted) return;

        const pendingCases = cases.filter((item) => isPendingLawyerConfirmationCase(item, userData));

        setRows(pendingCases.map(mapPblCaseToOcPendingReviewCase));
        setTotalEntries(pendingCases.length);
        setCurrentCaseCount(cases.filter(isLawyerCurrentCase).length);
      } catch (error: any) {
        if (isMounted) {
          errorHandler(error.response?.data?.message || error.response?.data?.data || error.message || t('pbl.pagesHardcoded.lawyerManageCases.pendingReviewList.failedToLoadPendingCases'));
        }
      }
    };

    loadCases();

    return () => {
      isMounted = false;
    };
  }, [userData]);

  const goToDetail = (item: PblOcPendingReviewCase) => {
    navigate(buildPendingDetailPath(String(item.id)));
  };

  return (
    <PblCaseList
      title={t('pbl.pagesHardcoded.lawyerManageCases.pendingReviewList.manageCase')}
      rows={rows}
      primaryAction={{
        label: t('pbl.pagesHardcoded.lawyerManageCases.pendingReviewList.currentCaseValue', { value1: currentCaseCount }),
        onClick: () => navigate(PATH.pbl.lawyerCases),
      }}
      totalEntries={totalEntries}
      backTo={PATH.pbl.lawyerCases}
      searchAriaLabel="Search pending review cases"
      onRowClick={goToDetail}
    />
  );
};

export default PblLawyerPendingReviewList;
